import TranslatedErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import { Button } from "@/shadcn/components/ui/button";
import { genericFailureToast } from "@/utils/utils.util";
import { ChevronLeft, ChevronRight, Minus, Plus } from "lucide-react";
import React, { useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ViewPDFProps = {
  fileBase64: string; // URL of the PDF file to be displayed
  fileName?: string; // Name of the PDF file for download
  closeViewer: () => void;
};

const ViewPDF: React.FC<ViewPDFProps> = ({
  fileBase64,
  closeViewer,
  fileName,
}) => {
  function base64ToBlob(
    base64: string,
    contentType: string = "application/pdf",
    sliceSize: number = 512
  ): Blob | undefined {
    try {
      const byteCharacters = atob(base64);
      // The reason for using plain Uint8Array is to guarantee that the Blob constructor receives the correct type, avoiding type errors caused by parameterized Uint8Array<ArrayBufferLike>.
      const byteArrays: Uint8Array[] = [];
      for (
        let offset = 0;
        offset < byteCharacters.length;
        offset += sliceSize
      ) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);
        const byteNumbers: number[] = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        // This ensures each chunk is a Uint8Array, which Blob accepts, preventing type incompatibility.
        byteArrays.push(new Uint8Array(byteNumbers));
      }
      return new Blob(byteArrays as any, { type: contentType });
    } catch (error) {
      console.error("Error converting base64 to Blob:", error);
      return undefined; // Return undefined if conversion fails
    }
  }

  const blobFile = useMemo(() => {
    const toB64 = base64ToBlob(fileBase64);
    if (!toB64) {
      console.error("Failed to convert base64 to Blob");
      return undefined;
    }
    return toB64;
  }, [fileBase64]);

  // Calculate PDF canvas size based on window size to ensure the PDF fits responsively.
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  React.useEffect(() => {
    function handleResize() {
      // Use 90% of the window's width and height so the PDF never overflows the viewport.
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    // Set initial size and clean up on unmount to avoid memory leaks.
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Track current page and total pages for navigation.
  const [numPages, setNumPages] = React.useState<number>(0);
  const [pageNumber, setPageNumber] = React.useState<number>(1);

  // Track zoom level for in/out controls.
  const [zoom, setZoom] = React.useState<number>(1);

  // Update total pages on document load.
  const onDocumentLoadSuccess = React.useCallback((pdf: any) => {
    // We store the number of pages so navigation controls know the bounds.
    setNumPages(pdf.numPages);
    setPageNumber(1); // Reset to first page on new document.
  }, []);

  // Store the natural PDF page size to calculate scaling that fits the window while preserving aspect ratio.
  const [pageSize, setPageSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  // Callback to get the PDF page's natural size (in points) so we can scale it to fit the window.
  const onPageLoadSuccess = React.useCallback((page: any) => {
    // We use page.originalWidth and page.originalHeight to get the actual PDF page size.
    // This is necessary to maintain aspect ratio and ensure the PDF never exceeds the window size, regardless of orientation.
    setPageSize({ width: page.originalWidth, height: page.originalHeight });
  }, []);

  // Calculate width based on zoom and window, so zooming is always relative to fit.
  const computedWidth = React.useMemo(() => {
    if (!pageSize) return undefined;
    const windowRatio = dimensions.width / dimensions.height;
    const pageRatio = pageSize.width / pageSize.height;
    let baseWidth;
    if (pageRatio > windowRatio) {
      baseWidth = Math.min(dimensions.width, pageSize.width);
    } else {
      baseWidth = Math.min(dimensions.height * pageRatio, dimensions.width);
    }
    // Multiply by zoom to allow user-controlled scaling, but never exceed window.
    return Math.min(baseWidth * zoom, dimensions.width);
  }, [dimensions, pageSize, zoom]);

  // Navigation handlers for page and zoom controls.
  const goToPrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () =>
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  const zoomIn = () => setZoom((z) => Math.min(z + 0.1, 3)); // Limit max zoom for usability.
  const zoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.3)); // Limit min zoom for usability.
  const resetZoom = () => setZoom(1);

  // Track pan/drag state for moving the PDF when zoomed in.
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [offset, setOffset] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  // Handlers for mouse/touch drag events to allow panning when zoomed in.
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only enable dragging if zoomed in (so the document is larger than the viewport).
    // if (zoom <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    // Prevent default to avoid unwanted text selection.
    e.preventDefault();
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart) return;
    // Update offset based on mouse movement to pan the document.
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };
  const onMouseUp = () => {
    setIsDragging(false);
    setDragStart(null);
  };
  // Touch support for mobile devices.
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - offset.x, y: touch.clientY - offset.y });
  };
  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStart) return;
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };
  const onTouchEnd = () => {
    setIsDragging(false);
    setDragStart(null);
  };

  // Reset pan when zoom changes or page changes, so the user always starts centered.
  React.useEffect(() => {
    setOffset({ x: 0, y: 0 });
  }, [zoom, pageNumber]);

  // Handler to trigger PDF download for the user.
  const handleDownload = () => {
    try {
      if (!blobFile) {
        console.error("No PDF file available for download");
        genericFailureToast(
          "No PDF file available for download. Please contact support."
        );
        return;
      }

      // Create a temporary URL for the Blob so the browser can download it.
      const url = URL.createObjectURL(blobFile);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName || "document"}.pdf`; // Use provided fileName or default to "document.pdf"
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke the object URL to free memory.
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      genericFailureToast(
        "Failed to download the PDF. Please contact support."
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex h-svh w-svw flex-col items-center justify-center overflow-auto bg-black/80">
      {/* Navigation and zoom controls are provided so users can interact with the PDF. */}
      <div className="fixed top-0 right-0 left-0 z-10 flex items-center justify-center gap-2 bg-black/50 p-2 text-white shadow-md select-none">
        <Button
          onClick={goToPrevPage}
          disabled={pageNumber <= 1}
          variant={"secondary"}
          className="bg-neutral-800 select-none hover:bg-neutral-700"
          size={"sm"}
        >
          <ChevronLeft />
        </Button>
        <span>
          Page {pageNumber} / {numPages || "?"}
        </span>
        <Button
          onClick={goToNextPage}
          disabled={pageNumber >= numPages}
          variant={"secondary"}
          className="bg-neutral-800 select-none hover:bg-neutral-700"
          size={"sm"}
        >
          <ChevronRight />
        </Button>
        <span className="mx-4 hidden md:block">|</span>
        <Button
          onClick={zoomOut}
          disabled={zoom <= 0.3}
          variant={"secondary"}
          className="hidden bg-neutral-800 select-none hover:bg-neutral-700 md:block"
          size={"sm"}
        >
          <Minus />
        </Button>
        <span className="hidden select-none md:block">
          Zoom: {(zoom * 100).toFixed(0)}%
        </span>
        <Button
          onClick={zoomIn}
          disabled={zoom >= 3}
          variant={"secondary"}
          className="hidden bg-neutral-800 select-none hover:bg-neutral-700 md:block"
          size={"sm"}
        >
          <Plus />
        </Button>
        <Button
          onClick={resetZoom}
          disabled={zoom === 1}
          variant={"secondary"}
          className="hidden bg-neutral-800 select-none hover:bg-neutral-700 md:block"
          size={"sm"}
        >
          Reset
        </Button>
        <Button
          onClick={handleDownload}
          variant={"secondary"}
          className="bg-neutral-800 select-none hover:bg-neutral-700"
          size={"sm"}
        >
          Download
        </Button>
        <Button
          onClick={() => {
            closeViewer();
          }}
          variant={"secondary"}
          className="bg-neutral-800 select-none hover:bg-neutral-700"
          size={"sm"}
        >
          Close
        </Button>
      </div>
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <TranslatedErrorBoundary
          fallback={
            <div className="rounded bg-white p-4 text-center text-red-600 shadow-md">
              <p>
                Unable to load the quote. Please try again or download the file.
              </p>
            </div>
          }
        >
          <div
            className="relative flex h-full w-full flex-1 items-center justify-center select-none"
            style={{
              cursor:
                zoom > 1 && isDragging
                  ? "grabbing"
                  : zoom > 1
                    ? "grab"
                    : "default",
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div
              style={{
                // We use CSS transform to move the PDF canvas, so the user can pan around when zoomed in.
                transform: `translate(${offset.x}px, ${offset.y}px)`,
                transition: isDragging ? "none" : "transform 0.2s",
                // Prevent the PDF from being dragged out of view by clamping the offset.
                // This is not strictly necessary for basic panning, but improves UX.
                maxWidth: "none",
                maxHeight: "none",
                willChange: "transform",
              }}
            >
              {blobFile ? (
                <Document
                  file={blobFile}
                  onLoadError={(error) =>
                    console.error("Error loading PDF:", error)
                  }
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={computedWidth}
                    onLoadSuccess={onPageLoadSuccess}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                  {/* Only width is set, because react-pdf ignores height if both are set. This ensures the PDF never exceeds the window in any direction. */}
                </Document>
              ) : (
                <div className="rounded bg-white p-4 text-center text-red-600 shadow-md">
                  <p>
                    Unable to load the quote. Please try again or download the
                    file.
                  </p>
                </div>
              )}
            </div>
          </div>
        </TranslatedErrorBoundary>
      </div>
    </div>
  );
};

export default ViewPDF;
